import { useRollbar } from '@rollbar/react'
import {
  TiptapEditor,
  TiptapEditorUpdatePayload,
} from 'client/components/core/tiptap-editor'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { useManagement } from 'client/store'
import managementSelectors from 'client/store/management/managementSelectors'
import { updateEntity } from 'client/store/page/pageActions'
import { TextInterface } from 'common/types/entities/TextInterface'
import React, { memo, useEffect, useState } from 'react'
import { useThemeColor } from 'src/editor/shared/hooks/use-theme-color'
import { useTypography } from 'src/editor/shared/hooks/use-typography'
import BaseEntity from '../../core/BaseEntity/BaseEntityNew'
import TextErrorBoundary from './TextErrorBoundary'

type TextProps = {
  entity: TextInterface
  isMoveUpAllowed: boolean
  isMoveDownAllowed: boolean
}

function Text({ entity, isMoveUpAllowed, isMoveDownAllowed }: TextProps) {
  const dispatch = useAppDispatch()
  const rollbar = useRollbar()
  const idEditing = useManagement(m =>
    managementSelectors.isEditing(m, entity.id),
  )
  const [isDebug, setDebug] = useState(false)
  const { isMobile } = useDeviceModeContext()
  const {
    text: { paragraphTextColor, paragraphBackgroundColor },
    links: { linkTextColor },
  } = useThemeColor()

  const {
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    calculatedLetterSpacing,
    calculatedFontFamily,
    calculatedFontWeight,
    calculatedFontStyle,
  } = useTypography({
    fontSize: entity.fontSize,
    mobileFontSize: entity.mobileFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    textAlign: entity.textAlign,
    mobileTextAlign: entity.mobileTextAlign,
    letterSpacing: entity.letterSpacing,
    mobileLetterSpacing: entity.mobileLetterSpacing,
    fontFamily: entity.fontFamily,
    mobileFontFamily: entity.mobileFontFamily,
    fontWeight: entity.fontWeight,
    mobileFontWeight: entity.mobileFontWeight,
    fontStyle: entity.fontStyle,
    mobileFontStyle: entity.mobileFontStyle,
  })

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location.search)
    const isDebug = searchParams.get('debug')
    setDebug(Boolean(isDebug))
  }, [])

  function update(payload: TiptapEditorUpdatePayload) {
    if (isDebug) {
      rollbar.info(`entity ${entity.id} update`, {
        entity: JSON.stringify({
          ...entity,
          ...payload,
        }),
      })
    }

    dispatch(
      updateEntity({
        ...entity,
        ...payload,
      }),
    )
  }

  return (
    <BaseEntity
      entity={entity}
      isMoveUpAllowed={isMoveUpAllowed}
      isMoveDownAllowed={isMoveDownAllowed}
    >
      <TextErrorBoundary entity={entity}>
        <TiptapEditor
          isEditing={idEditing}
          update={update}
          attrId={entity.htmlAttrId}
          padding={entity.padding}
          textAlign={calculatedTextAlign.desktop}
          editorTextAlign={
            isMobile ? calculatedTextAlign.mobile : calculatedTextAlign.desktop
          }
          textColor={paragraphTextColor}
          linkColor={linkTextColor}
          fontSize={calculatedFontSize.desktop}
          fontStyle={calculatedFontStyle.desktop}
          fontFamily={calculatedFontFamily.desktop}
          fontWeight={calculatedFontWeight.desktop}
          lineHeight={calculatedLineHeight.desktop}
          mobilePadding={entity.mobilePadding}
          mobileFontSize={calculatedFontSize.mobile}
          mobileLineHeight={calculatedLineHeight.mobile}
          mobileFontStyle={calculatedFontStyle.mobile}
          mobileFontFamily={calculatedFontFamily.mobile}
          mobileFontWeight={calculatedFontWeight.mobile}
          backgroundColor={entity.backgroundColor || paragraphBackgroundColor}
          mobileBackgroundColor={
            entity.mobileBackgroundColor || paragraphBackgroundColor
          }
          letterSpacing={calculatedLetterSpacing.desktop}
          mobileLetterSpacing={calculatedLetterSpacing.mobile}
          content={entity.content || entity.rawContentState || ''}
        />
      </TextErrorBoundary>
    </BaseEntity>
  )
}

export function getTextFontProperties(entity: TextInterface) {
  const fontProperties = []

  if (entity.fontFamily) {
    fontProperties.push({
      fontFamily: entity.fontFamily,
      fontStyle: entity.fontStyle,
      fontWeight: entity.fontWeight,
    })
  }

  if (entity.mobileFontFamily) {
    fontProperties.push({
      fontFamily: entity.mobileFontFamily,
      fontStyle: entity.mobileFontStyle,
      fontWeight: entity.mobileFontWeight,
    })
  }
  if (
    entity.fontFamily &&
    (entity.rawContentState?.includes('"style":"BOLD"') ||
      entity.content?.includes('<strong>'))
  ) {
    fontProperties.push({
      fontFamily: entity.fontFamily,
      fontStyle: 'normal',
      fontWeight: '700',
    })
  }

  if (
    entity.fontFamily &&
    (entity.rawContentState?.includes('"style":"ITALIC"') ||
      entity.content?.includes('<em>'))
  ) {
    fontProperties.push({
      fontFamily: entity.fontFamily,
      fontStyle: 'italic',
      fontWeight: entity.fontWeight,
    })
  }

  return fontProperties
}

export function getTextFontVariants(entity: TextInterface) {
  const fontProperties = []
  if (entity.fontFamily) {
    fontProperties.push({
      fontFamily: entity.fontFamily,
      fontStyle: entity.fontStyle,
      fontWeight: entity.fontWeight,
    })
  }

  if (entity.mobileFontFamily) {
    fontProperties.push({
      fontFamily: entity.mobileFontFamily,
      fontStyle: entity.mobileFontStyle,
      fontWeight: entity.mobileFontWeight,
    })
  }

  if (entity.content?.includes('<strong>')) {
    fontProperties.push({
      fontFamily: entity.fontFamily,
      fontStyle: 'normal',
      fontWeight: '700',
    })
  }

  if (entity.content?.includes('<em>')) {
    fontProperties.push({
      fontFamily: entity.fontFamily,
      fontStyle: 'italic',
      fontWeight: entity.fontWeight,
    })
  }

  return fontProperties
}

export default memo(Text)
